import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/faq.css";

/* ---------- Content: edit these ---------- */
const categories = [
  "All",
  "General",
  "Services",
  "Support",
  "Security",
  "Careers",
];

const faqs = [
  {
    category: "General",
    q: "What does Global Tech9 LLC do?",
    a: "Global Tech9 LLC is an IT services company. We help businesses with managed IT, IT support, IT consultancy, cloud computing and cyber security, so your team can focus on running the business while we look after the technology.",
  },
  {
    category: "General",
    q: "Where are you located?",
    a: "Our office is at 5900 Balcones Drive STE 100, Austin, TX 78731, USA.",
  },
  {
    category: "General",
    q: "What are your working hours?",
    a: "Our team is available Monday to Saturday, 8:00 AM to 6:00 PM. Call or email us during these hours and we will get back to you.",
  },
  {
    category: "General",
    q: "How can I contact you?",
    a: "You can contact us on, email contact@globaltech9.com for sales enquiries or support@globaltech9.com for support, or use the Contact Us page on our website.",
  },
  {
    category: "General",
    q: "How much do your services cost?",
    a: "Cost depends on the size of your environment and the services you choose. Contact us and, after understanding your needs, we will prepare a proposal that fits your business.",
  },
  {
    category: "Services",
    q: "Which IT services do you offer?",
    a: "We offer managed IT, IT support, IT consultancy, cloud computing and cyber security. You can use one service on its own or combine them, depending on what your business needs.",
  },
  {
    category: "Services",
    q: "What is managed IT, and is it right for my business?",
    a: "Managed IT means we look after your day-to-day technology, including monitoring, maintenance, updates and help for your users, instead of you reacting to each problem as it appears. It suits growing businesses that want reliable IT without building a full in-house team.",
  },
  {
    category: "Services",
    q: "Can you help us move to the cloud?",
    a: "Yes, cloud computing is one of our services. We start by understanding your current systems and goals, then plan the move step by step so your team can keep working while workloads are migrated.",
  },
  {
    category: "Services",
    q: "Do you provide IT consultancy?",
    a: "Yes. We can review your current setup, point out gaps and risks, and give you a practical plan that matches your size and budget.",
  },
  {
    category: "Support",
    q: "Do you offer remote and onsite support?",
    a: "Yes. Our IT team provides remote and onsite hands-on support to staff, vendors and customers, and works to identify issues before they turn into bigger problems.",
  },
  {
    category: "Support",
    q: "How do I request support?",
    a: "Call us or email support@globaltech9.com during working hours. Tell us what you are seeing, which device or system is affected and how urgent it is, and we will take it from there.",
  },
  {
    category: "Support",
    q: "What happens when I contact you about my plan?",
    a: "We offer a detailed analysis and assessment of your plan. Our team will understand your requirements, review your current environment and suggest the next steps.",
  },
  {
    category: "Security",
    q: "How do you approach cyber security?",
    a: "We begin with an assessment of your current setup: sign-in and access, devices, networks and backups. Then we recommend practical, prioritised steps that reduce risk without slowing your team down.",
  },
  {
    category: "Security",
    q: "Is my business too small to need cyber security?",
    a: "No. Small and mid-size businesses are frequent targets because they often have fewer protections in place. Basics such as multi-factor authentication, regular updates and tested backups make a big difference.",
  },
  {
    category: "Careers",
    q: "How can I apply for a job at Global Tech9 LLC?",
    a: "Visit our Careers page, choose an open position and click Apply now. You will be asked for your name, email, phone number, years of experience and a link to your resume on Google Drive.",
  },
  {
    category: "Careers",
    q: "What if there is no position that suits me?",
    a: "You can still send your details using the same form on the Careers page, without choosing a position. We keep them on file and contact you when a suitable role opens up.",
  },
];

const contact = {
  phoneDisplay: "",
  phoneHref: "tel:+917977353388",
  bigPhone: "79773 53388",
  email: "support@globaltech9.com",
  hours: "Mon to Sat, 8:00 AM - 6:00 PM",
};

/* ---------- Icons ---------- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};

const Icon = {
  crumb: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2.2">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" />
    </svg>
  ),
  empty: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.6">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4M8.5 11h5" />
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M3 6h18v12H3zM3 7l9 6 9-6" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
};

/* One accordion row */
function Item({ faq, number, open, onToggle }) {
  const uid = useId();
  const qId = `q-${uid}`;
  const aId = `a-${uid}`;
  return (
    <div className={`gt9-qa${open ? " is-open" : ""}`}>
      <h3>
        <button
          type="button"
          className="gt9-qa__q"
          id={qId}
          aria-expanded={open}
          aria-controls={aId}
          onClick={onToggle}
        >
          <span className="gt9-qa__num" aria-hidden="true">
            {String(number).padStart(2, "0")}
          </span>
          <span className="gt9-qa__text">
            <span className="gt9-qa__tag">{faq.category}</span>
            <span className="gt9-qa__qt">{faq.q}</span>
          </span>
          <span className="gt9-qa__icon" aria-hidden="true" />
        </button>
      </h3>
      <div className="gt9-qa__a" id={aId} role="region" aria-labelledby={qId}>
        <div>
          <p>{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Faqs() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [openIdx, setOpenIdx] = useState(() => new Set([0])); // first question open by default

  // keep each question's number stable while filtering
  const numbered = useMemo(
    () => faqs.map((f, i) => ({ ...f, n: i + 1, id: i })),
    [],
  );
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return numbered.filter(
      (f) =>
        (cat === "All" || f.category === cat) &&
        (!term || `${f.category} ${f.q} ${f.a}`.toLowerCase().includes(term)),
    );
  }, [numbered, cat, q]);

  const toggle = (id) =>
    setOpenIdx((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const clear = () => {
    setCat("All");
    setQ("");
  };

  return (
    <main className="gt9-faq">
      {/* Hero */}
      <section className="gt9-faq__hero">
        <div className="gt9-container">
          <ol className="gt9-crumbs" aria-label="Breadcrumb">
            <li>
              <Link to="/">Home</Link>
              {Icon.crumb}
            </li>
            <li aria-current="page">FAQs</li>
          </ol>
          <span className="gt9-eyebrow">FAQs</span>
          <h1 className="gt9-faq__title">
            Questions we hear <span className="hl">most often</span>
          </h1>
          <p className="gt9-faq__lead">
            Quick answers about our services, support and how we work. Can't
            find what you need? Our team is a call away.
          </p>
        </div>
      </section>

      <div className="gt9-faq__body">
        <div className="gt9-container">
          {/* Search + topic filters */}
          <div className="gt9-bar2">
            <label className="gt9-search">
              {Icon.search}
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search questions"
                aria-label="Search questions"
                autoComplete="off"
              />
            </label>
            <div
              className="gt9-chips"
              role="group"
              aria-label="Filter by topic"
            >
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`gt9-chip${c === cat ? " is-active" : ""}`}
                  aria-pressed={c === cat}
                  onClick={() => setCat(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="gt9-meta">
            <p aria-live="polite" style={{ margin: 0 }}>
              {list.length === 0 ? (
                "No questions match your search"
              ) : (
                <>
                  Showing <b>{list.length}</b> of <b>{faqs.length}</b> questions
                  {cat !== "All" && (
                    <>
                      {" "}
                      in <b>{cat}</b>
                    </>
                  )}
                </>
              )}
            </p>
          </div>

          <div className="gt9-faq__layout">
            <div>
              {list.length === 0 ? (
                <div className="gt9-empty">
                  {Icon.empty}
                  <h3>No questions found</h3>
                  <p>Try a different keyword or pick another topic.</p>
                  <button type="button" onClick={clear}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="gt9-list">
                  {list.map((f) => (
                    <Item
                      key={f.id}
                      faq={f}
                      number={f.n}
                      open={openIdx.has(f.id)}
                      onToggle={() => toggle(f.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            <aside className="gt9-help" aria-label="Contact">
              <h3>Still have questions?</h3>
              <p>
                Our IT team is happy to help with anything not covered here.
              </p>
              <ul>
                <li>
                  {Icon.phone}
                  <div>
                    <b>Call us</b>
                    <a href={" "}> </a>
                  </div>
                </li>
                <li>
                  {Icon.mail}
                  <div>
                    <b>Email support</b>
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </div>
                </li>
                <li>
                  {Icon.clock}
                  <div>
                    <b>Working hours</b>
                    {contact.hours}
                  </div>
                </li>
              </ul>
              <Link to="/contact-us" className="gt9-btn">
                Contact us {Icon.arrow}
              </Link>
            </aside>
          </div>

          {/* Contact section */}
          <section className="gt9-cta3" aria-labelledby="gt9-cta-title">
            <div>
              <h2 id="gt9-cta-title">
                Get further information by{" "}
                <span className="hl">making contact</span> with our experienced
                IT staff.
              </h2>
              <p className="gt9-cta3__text">
                <b>We're available for 8 hours a day!</b>
                Contact us to request a detailed analysis and assessment of your
                plan.
              </p>
            </div>
            <div className="gt9-reach">
              <span className="gt9-reach__icon">{Icon.phone}</span>
              <span className="gt9-reach__label">REACH OUT NOW!</span>
              <a className="gt9-reach__phone" href={" "}>
                {" "}
              </a>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
